import { type ChangeEvent, type ReactNode, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, useToast } from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { ImageCropDialog } from './ImageCropDialog.js';
import { BANNER_IMAGE_TYPES, bannerImageProblem } from './BannerImageControl.js';

/**
 * Kategoriya qatoridagi rasm bloki. Fayl qoidalari banner bilan bir xil
 * (JPEG/PNG/WebP/AVIF, ≤ 12 MB); server 256×256 WebP'ga keltiradi.
 * Rasm bo'lmasa mijoz ilovasida kategoriya ikonkasi chiqadi.
 */
export function CategoryImageControl({
  categoryId,
  imageUrl,
  name,
}: {
  categoryId: string;
  imageUrl: string | null;
  name: string;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  // Tanlangan fayl kesish oynasida turadi; yuklash faqat u yopilgach.
  const [cropping, setCropping] = useState<File | null>(null);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['categories'] });
  };
  const fail = (error: unknown): void => {
    toast.show(error instanceof ApiError ? error.text(locale) : t.errorTitle, 'danger');
  };

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadCategoryImage(categoryId, file),
    onSuccess: () => {
      toast.show(t.refCategoryImageDone, 'success');
      refresh();
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => api.deleteCategoryImage(categoryId),
    onSuccess: () => {
      toast.show(t.refCategoryImageRemoved, 'success');
      refresh();
    },
    onError: fail,
  });

  const onPick = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file === undefined) return;
    const problem = bannerImageProblem(file);
    if (problem !== null) {
      toast.show(problem === 'type' ? t.refBannerImageType : t.refBannerImageTooBig, 'danger');
      return;
    }
    setCropping(file);
  };

  const busy = upload.isPending || remove.isPending;

  return (
    <div className="adm-banner-image">
      {imageUrl === null ? null : (
        <img
          className="adm-banner-image__preview"
          src={imageUrl}
          alt={`${t.refCategoryImage}: ${name}`}
          width={56}
          height={56}
        />
      )}
      <div className="adm-banner-image__actions">
        <input
          ref={input}
          type="file"
          accept={BANNER_IMAGE_TYPES.join(',')}
          hidden
          aria-label={`${t.refCategoryImage}: ${name}`}
          onChange={onPick}
        />
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => {
            input.current?.click();
          }}
        >
          {imageUrl === null ? t.refCategoryImageUpload : t.refCategoryImageReplace}
        </Button>
        {imageUrl === null ? null : (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              remove.mutate();
            }}
          >
            {t.refCategoryImageRemove}
          </Button>
        )}
        <span className="aps-muted">{t.refCategoryImageHint}</span>
      </div>

      {cropping !== null && (
        <ImageCropDialog
          key={cropping.name + String(cropping.size)}
          file={cropping}
          position={{ index: 1, total: 1 }}
          aspects={['square']}
          frameLongSide={340}
          onDone={(file) => {
            setCropping(null);
            upload.mutate(file);
          }}
          onSkip={() => {
            setCropping(null);
            upload.mutate(cropping);
          }}
          onCancel={() => {
            setCropping(null);
          }}
        />
      )}
    </div>
  );
}
